import React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import OfferAPI from "@/src/api/OfferAPI";
import { PROJECTS_ROUTE } from "@/src/components/routes/routes";
import Spinner from "@/src/components/utils/Spinner";

import FreecopyForm from "./FreecopyForm";
import { toFreecopyCreate, toFreecopyUpdate } from "./freecopyFormSchema";

import type { JSX } from "react";

import type { AppState } from "@/src/reducers";

import type { FreecopyFormValues } from "./freecopyFormSchema";

const FALLBACK_LANG = "en";

export function FreecopyPage(): JSX.Element {
  const { t } = useTranslation("editor-notif");
  const { projectSlug, action, id } = useParams<{
    projectSlug: string;
    action?: string;
    id?: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();

  const isEdit = action === "edit" && Boolean(id);
  const listRoute = `${PROJECTS_ROUTE}/${projectSlug}/freecopy`;

  const defaultLang = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.translations?.find(
        (translation) => translation.default,
      )?.language ?? FALLBACK_LANG,
  );

  const { data: offerPlansData } = useQuery(
    OfferAPI.generateQuery({
      fn: OfferAPI.getOfferPlans,
      params: [{ projectSlug }],
    }),
  );

  const getFreecopyQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getFreecopy,
    params: [{ uuid: id ?? "", projectSlug }],
  });

  const { data: freecopy, isLoading: isLoadingFreecopy } = useQuery({
    ...getFreecopyQuery,
    onError: () => {
      toast.error(t("fetchFreecopyError"));
      history.push(listRoute);
    },
    enabled: isEdit,
  });

  const onSuccess = (): void => {
    if (isEdit) {
      queryClient.invalidateQueries({ queryKey: getFreecopyQuery.queryKey });
    }
    queryClient.invalidateQueries({
      queryKey: OfferAPI.generateQuery({
        fn: OfferAPI.getFreecopies,
        params: [{ projectSlug }],
      }).queryKey,
    });
    toast.success(t("saveSuccess"));
    history.push(listRoute);
  };

  const onError = (): void => {
    toast.error(t("saveError"));
  };

  const updateFreecopy = useMutation({
    mutationFn: async (params: Parameters<typeof OfferAPI.updateFreecopy>[0]) =>
      OfferAPI.updateFreecopy(params),
    onSuccess,
    onError,
  });

  const createFreecopy = useMutation({
    mutationFn: async (params: Parameters<typeof OfferAPI.createFreecopy>[0]) =>
      OfferAPI.createFreecopy(params),
    onSuccess,
    onError,
  });

  const onSubmit = (values: FreecopyFormValues): void => {
    if (isEdit && freecopy) {
      updateFreecopy.mutate({
        uuid: freecopy.uuid,
        data: toFreecopyUpdate(values, freecopy),
        projectSlug,
      });
      return;
    }

    createFreecopy.mutate({ data: toFreecopyCreate(values), projectSlug });
  };

  if (isEdit && isLoadingFreecopy) {
    return (
      <div
        className="flex w-full justify-center p-10"
        data-testid="FreecopyPage"
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="FreecopyPage">
      <FreecopyForm
        defaultLang={defaultLang}
        freecopy={freecopy}
        isEditMode={isEdit}
        isLoading={updateFreecopy.isLoading || createFreecopy.isLoading}
        offerPlans={offerPlansData?.data}
        onSubmit={onSubmit}
      />
    </div>
  );
}

export default FreecopyPage;
